import { Rng, Sfc32Rng } from '../Rng';
import { fromJsonNumber, jsonNumber } from '../snapshot';

export enum AntRole {
  QUEEN = 'queen',
  FORAGER = 'forager',
  SOLDIER = 'soldier',
  NURSE = 'nurse',
  ENGINEER = 'engineer',
}

/**
 * Scent types used for biologically-plausible ant navigation.
 * Ants follow scent gradients rather than explicit paths.
 */
export enum ScentType {
  FOOD_TRAIL = 'food_trail',     // Deposited by foragers returning with food
  BROOD_SCENT = 'brood_scent',   // Emanates from eggs, larvae, pupae
  QUEEN_SCENT = 'queen_scent',   // Emanates from queen chamber (home beacon)
  ALARM = 'alarm',               // Deposited during threats, attracts soldiers
  NEST_ODOR = 'nest_odor',       // General colony scent, stronger deeper
  FOOD_SMELL = 'food_smell',     // Emanates from food storage
}

/**
 * `T` must be `never`, so instantiating this with a non-empty union fails to
 * compile and the error names the members that are missing.
 *
 * This is what makes a named iteration order (rule 4) safe to rely on: the list
 * is the promise, and this is the check that the promise still covers everything
 * it claims to.
 */
export type AssertExhaustive<T extends never> = T;

/**
 * The canonical iteration order for scent types. Same promise as
 * `TASK_TYPE_ORDER` below, for the same reason.
 *
 * Diffusion and decay walk this list. Each scent reads and writes only its own
 * channel, so the order is *currently* invisible — and that is exactly the
 * condition under which `Object.values(ScentType)` survived four determinism
 * passes. The day one scent's flow reads another's level, the enum's declaration
 * order would silently become part of the simulation.
 */
export const SCENT_TYPE_ORDER = Object.freeze([
  ScentType.FOOD_TRAIL,
  ScentType.BROOD_SCENT,
  ScentType.QUEEN_SCENT,
  ScentType.ALARM,
  ScentType.NEST_ODOR,
  ScentType.FOOD_SMELL,
] as const) satisfies readonly ScentType[];

type _AllScentsListed = AssertExhaustive<
  Exclude<ScentType, (typeof SCENT_TYPE_ORDER)[number]>
>;

export enum AntState {
  IDLE = 'idle',
  WALKING = 'walking',
  FORAGING = 'foraging',
  CARRYING = 'carrying',
  NURSING = 'nursing',
  GUARDING = 'guarding',
  RESTING = 'resting',
  FLEEING = 'fleeing',
  DYING = 'dying',
  DEAD = 'dead',
  CONTEMPLATING = 'contemplating', // The philosopher ant
}

/**
 * Task enum - represents PHYSICAL STATE, not intentions.
 * Ants follow scents and react locally; tasks track what they're carrying/doing.
 */
export enum AntTask {
  NONE = 'none',                      // Following scents, reacting locally

  // Carrying states (physical)
  CARRYING_EGG = 'carrying_egg',       // Has egg, following BROOD_SCENT
  CARRYING_FOOD = 'carrying_food',     // Has food, following scent to destination

  // Surface states
  ON_SURFACE = 'on_surface',           // Foraging or returning on surface

  // Stationary/working states
  GUARDING = 'guarding',               // Soldier at entrance
  PATROLLING = 'patrolling',           // Soldier/engineer moving through colony
  REPAIRING = 'repairing',             // Engineer fixing tunnel/chamber

  // Special states
  RESPONDING_ALARM = 'responding_alarm', // Rushing to threat
  TRANSITIONING = 'transitioning',       // Worker becoming queen
}

export interface Position {
  x: number;
  y: number;
}

export interface AntMemory {
  type: 'food_source' | 'danger' | 'death' | 'path';
  position: Position;
  timestamp: number;
  detail?: string;
}

export interface AntRelationship {
  antId: number;
  type: 'trained_by' | 'trained' | 'sibling' | 'worked_with';
  strength: number; // 0-1
}

/**
 * A role an ant used to have.
 *
 * Recorded when it transitions, and read to name it: an ant's third career shows
 * up in the name it is given. Lives here rather than in `RoleTransitionSystem`
 * because it describes an ant's history rather than the system's opinion of it.
 */
export interface PreviousRole {
  role: AntRole;
  name: string;
  age: number;
  stats: AntStats;
  transitionTime: number;
}

/** What an engineer is on its way to fix. */
export interface RepairTarget {
  type: string;
  id: number;
}

export interface AntStats {
  foragingTrips: number;
  foodGathered: number;
  distanceTraveled: number;
  threatsRepelled: number;
  eggsNursed: number;
  tunnelsDug: number;
}

/**
 * Task threshold identifiers for response-threshold model.
 * Each ant has personal thresholds for each task type.
 */
export enum TaskType {
  FORAGE = 'forage',           // Go to surface for food
  FEED_LARVAE = 'feed_larvae', // Feed hungry larvae
  FEED_QUEEN = 'feed_queen',   // Feed the queen
  CARRY_EGG = 'carry_egg',     // Move eggs to nursery
  TRANSPORT = 'transport',     // Move food/resources between chambers
  GUARD = 'guard',             // Guard entrances
  PATROL = 'patrol',           // Patrol the colony
  REPAIR = 'repair',           // Fix collapsed tunnels
  CLEAN = 'clean',             // Clean chambers, remove waste
  REST = 'rest',               // Rest when fatigued
}

/**
 * The canonical iteration order for task types.
 *
 * Anything that draws randomness per task must walk this list, never
 * `Object.values(TaskType)`. The two agree today; only this one is a promise.
 */
export const TASK_TYPE_ORDER = Object.freeze([
  TaskType.FORAGE,
  TaskType.FEED_LARVAE,
  TaskType.FEED_QUEEN,
  TaskType.CARRY_EGG,
  TaskType.TRANSPORT,
  TaskType.GUARD,
  TaskType.PATROL,
  TaskType.REPAIR,
  TaskType.CLEAN,
  TaskType.REST,
] as const) satisfies readonly TaskType[];

type _AllTasksListed = AssertExhaustive<Exclude<TaskType, (typeof TASK_TYPE_ORDER)[number]>>;

/**
 * Per-ant task thresholds for response-threshold model.
 * Lower threshold = more likely to respond to stimulus.
 * Values typically 0.1 to 0.9.
 */
export interface TaskThresholds {
  [TaskType.FORAGE]: number;
  [TaskType.FEED_LARVAE]: number;
  [TaskType.FEED_QUEEN]: number;
  [TaskType.CARRY_EGG]: number;
  [TaskType.TRANSPORT]: number;
  [TaskType.GUARD]: number;
  [TaskType.PATROL]: number;
  [TaskType.REPAIR]: number;
  [TaskType.CLEAN]: number;
  [TaskType.REST]: number;
}

/**
 * Generate task thresholds based on role and some randomness.
 * This creates individual variation while maintaining role tendencies.
 *
 * Thresholds must be LOWER than baseline stimulus (0.15) for primary role tasks
 * to ensure spontaneous activity. Formula: desirability = stimulus - threshold - switching_cost
 * With baseline ~0.15 and switching_cost 0.08, primary thresholds should be < 0.07 for good activity.
 */
export function generateTaskThresholds(role: AntRole, rng: Rng): TaskThresholds {
  // Base thresholds by role (lower = more responsive)
  // Primary role tasks have very low thresholds to ensure activity
  const roleProfiles: Record<AntRole, Partial<TaskThresholds>> = {
    [AntRole.QUEEN]: {
      // Queen doesn't do tasks
      [TaskType.FORAGE]: 1.0,
      [TaskType.FEED_LARVAE]: 1.0,
      [TaskType.FEED_QUEEN]: 1.0,
      [TaskType.CARRY_EGG]: 1.0,
      [TaskType.TRANSPORT]: 1.0,
      [TaskType.GUARD]: 1.0,
      [TaskType.PATROL]: 1.0,
      [TaskType.REPAIR]: 1.0,
      [TaskType.CLEAN]: 1.0,
      [TaskType.REST]: 0.3,
    },
    [AntRole.FORAGER]: {
      [TaskType.FORAGE]: 0.05,       // PRIMARY: Very low - foragers forage!
      [TaskType.FEED_LARVAE]: 0.8,
      [TaskType.FEED_QUEEN]: 0.8,
      [TaskType.CARRY_EGG]: 0.9,
      [TaskType.TRANSPORT]: 0.35,    // Can help transport
      [TaskType.GUARD]: 0.7,
      [TaskType.PATROL]: 0.25,       // Can patrol when not foraging
      [TaskType.REPAIR]: 0.8,
      [TaskType.CLEAN]: 0.7,
      [TaskType.REST]: 0.5,
    },
    [AntRole.NURSE]: {
      [TaskType.FORAGE]: 0.9,
      [TaskType.FEED_LARVAE]: 0.04,  // PRIMARY: Very low - nurses feed larvae!
      [TaskType.FEED_QUEEN]: 0.06,   // PRIMARY: Very low - nurses feed queen!
      [TaskType.CARRY_EGG]: 0.03,    // PRIMARY: Very low - nurses carry eggs!
      [TaskType.TRANSPORT]: 0.5,
      [TaskType.GUARD]: 0.8,
      [TaskType.PATROL]: 0.7,
      [TaskType.REPAIR]: 0.8,
      [TaskType.CLEAN]: 0.25,        // Nurses also clean
      [TaskType.REST]: 0.6,
    },
    [AntRole.SOLDIER]: {
      [TaskType.FORAGE]: 0.8,
      [TaskType.FEED_LARVAE]: 0.9,
      [TaskType.FEED_QUEEN]: 0.7,
      [TaskType.CARRY_EGG]: 0.9,
      [TaskType.TRANSPORT]: 0.7,
      [TaskType.GUARD]: 0.08,        // PRIMARY: Very low - soldiers guard!
      [TaskType.PATROL]: 0.12,       // PRIMARY: Low - soldiers patrol!
      [TaskType.REPAIR]: 0.6,
      [TaskType.CLEAN]: 0.7,
      [TaskType.REST]: 0.5,
    },
    [AntRole.ENGINEER]: {
      [TaskType.FORAGE]: 0.7,
      [TaskType.FEED_LARVAE]: 0.8,
      [TaskType.FEED_QUEEN]: 0.8,
      [TaskType.CARRY_EGG]: 0.7,
      [TaskType.TRANSPORT]: 0.25,    // Good at transport
      [TaskType.GUARD]: 0.6,
      [TaskType.PATROL]: 0.15,       // PRIMARY: Engineers patrol to inspect
      [TaskType.REPAIR]: 0.02,       // PRIMARY: Very low - engineers repair!
      [TaskType.CLEAN]: 0.2,         // Good at maintenance
      [TaskType.REST]: 0.5,
    },
  };

  const base = roleProfiles[role];

  // Add individual variation - proportional to base value to preserve role identity
  // Low thresholds get small variation, high thresholds get more variation
  const thresholds: TaskThresholds = {} as TaskThresholds;
  // ORDERING: iterate a frozen, explicitly ordered task list rather than
  // Object.values(TaskType). The enum's order happens to be stable today, but
  // it is stable by accident of declaration order — reordering the enum for
  // readability would silently change every ant ever generated.
  for (const task of TASK_TYPE_ORDER) {
    const baseValue = base[task] ?? 0.5;
    // Variation is ±20% of base value, clamped to reasonable range
    const maxVariation = Math.min(0.1, baseValue * 0.4);
    const variation = (rng.next() - 0.5) * 2 * maxVariation;
    thresholds[task] = Math.max(0.01, Math.min(0.95, baseValue + variation));
  }

  return thresholds;
}

/**
 * Per-role physical attributes (configurable via settings).
 */
export interface AntRoleAttributes {
  maxAge: number;
  baseSpeed: number;
  size: number;
  maxHealth: number;
}

export const DEFAULT_ANT_ROLE_ATTRIBUTES: Record<AntRole, AntRoleAttributes> = {
  [AntRole.QUEEN]: { maxAge: Infinity, baseSpeed: 0.2, size: 2, maxHealth: 200 },
  [AntRole.SOLDIER]: { maxAge: 180000, baseSpeed: 1.2, size: 1.3, maxHealth: 150 },
  [AntRole.FORAGER]: { maxAge: 120000, baseSpeed: 1.0, size: 1, maxHealth: 100 },
  [AntRole.NURSE]: { maxAge: 150000, baseSpeed: 0.8, size: 0.9, maxHealth: 100 },
  [AntRole.ENGINEER]: { maxAge: 140000, baseSpeed: 0.9, size: 1.1, maxHealth: 100 },
};


export class Ant {
  readonly id: number;
  name: string;
  customName: string | null = null; // Player-given name
  readonly role: AntRole;
  readonly birthTick: number;

  // Position and movement
  position: Position;
  targetPosition: Position | null = null;
  speed: number;
  readonly baseSpeed: number;

  // State
  state: AntState = AntState.IDLE;
  previousState: AntState = AntState.IDLE;

  // Needs (0-100)
  hunger: number = 0;
  fatigue: number = 0;

  // Health
  health: number = 100;
  maxHealth: number = 100;
  injured: boolean = false;

  // Age in ticks (1 tick = ~16.67ms at 60fps)
  age: number = 0;
  readonly maxAge: number; // Lifespan varies by role

  // Social
  relationships: AntRelationship[] = [];
  memories: AntMemory[] = [];

  // Stats (for the player to discover)
  stats: AntStats = {
    foragingTrips: 0,
    foodGathered: 0,
    distanceTraveled: 0,
    threatsRepelled: 0,
    eggsNursed: 0,
    tunnelsDug: 0,
  };

  // Visual
  size: number = 1; // Multiplier for rendering
  scarred: boolean = false; // Visual indicator of past danger

  // Special flags
  isPhilosopher: boolean = false; // The one ant that contemplates

  // Response-threshold model additions
  taskThresholds: TaskThresholds;   // Personal task response thresholds
  taskCooldown: number = 0;         // Time until task switch allowed (ms)
  lastTaskSwitchTime: number = 0;   // When task was last changed
  currentStimulusResponse: number = 0; // Last computed task desirability (for debug)

  // Current task
  carryingFood: boolean = false;
  carryingEgg: boolean = false;

  // Task tracking
  task: AntTask = AntTask.NONE;
  carriedFoodAmount: number = 0; // 0-10 units
  carriedFoodHue: number = 20;   // Color hue of carried food (0-40, red to yellow)
  taskTimer: number = 0;         // Time spent on current task
  alarmPosition: Position | null = null; // Position of alarm (for soldiers)

  // Scent-based navigation (replaces path-based navigation)
  scentPreference: ScentType = ScentType.NEST_ODOR;  // Current scent to follow
  currentChamberId: number | null = null;             // Which chamber ant is in (null = in tunnel)
  currentTunnelId: number | null = null;              // Which tunnel ant is traversing (null = in chamber)
  recentTunnels: number[] = [];                       // Recently used tunnels (avoid backtracking)
  tunnelProgress: number = 0;                         // 0-1 progress through current tunnel
  movingTowardEnd: boolean = true;                    // Direction in tunnel (toward end vs start)

  // Chamber interaction timing
  chamberEntryTime: number = 0;                       // When ant entered current chamber (gameTimeMs)
  lastChamberChangeId: number | null = null;          // Track chamber transitions
  avoidChambersUntil: Map<number, number> = new Map(); // Chambers to avoid until time (full chambers)

  // Interaction target (for smooth pickup animations)
  interactionTargetId: number | null = null;          // Brood/food being approached
  interactionProgress: number = 0;                    // 0-1 progress toward interaction
  pickupChamberId: number | null = null;              // Chamber where item was picked up (don't drop there)

  /**
   * State the systems used to bolt on with `(ant as any).x = ...`.
   *
   * All four decide something later: the role history decides a transitioned
   * ant's name, the transition time decides whether it may transition again, the
   * repair target is what an engineer is walking towards, and the entrance flag
   * changes how navigation treats a homing forager.
   *
   * They are declared here because a snapshot cannot carry what the type system
   * does not admit exists — and none of them was admitted until `save()` went
   * looking. Written by `RoleTransitionSystem`, the engineer branch of
   * `FormicariumLayer.updateEngineer`, and `NavigationSystem` respectively.
   */
  previousRoles: PreviousRole[] = [];
  lastTransitionTime: number | null = null;
  repairTarget: RepairTarget | null = null;
  seekingEntrance = false;

  constructor(
    id: number,
    name: string,
    role: AntRole,
    position: Position,
    birthTick: number,
    rng: Rng,
    roleAttributes?: Record<AntRole, AntRoleAttributes>
  ) {
    this.id = id;
    this.name = name;
    this.role = role;
    this.position = { ...position };
    this.birthTick = birthTick;

    // Generate individual task thresholds based on role
    this.taskThresholds = generateTaskThresholds(role, rng);

    // Set role-specific attributes from provided or default attributes
    const attrs = (roleAttributes ?? DEFAULT_ANT_ROLE_ATTRIBUTES)[role];
    this.maxAge = attrs.maxAge;
    this.baseSpeed = attrs.baseSpeed;
    this.size = attrs.size;
    this.maxHealth = attrs.maxHealth;
    this.health = attrs.maxHealth;

    this.speed = this.baseSpeed;
  }

  getDisplayName(): string {
    return this.customName || this.name;
  }

  getAgeInDays(): number {
    // Convert ticks to "ant days" (1 ant day = ~10 real seconds for gameplay)
    return Math.floor(this.age / 600);
  }

  isElderly(): boolean {
    return this.age > this.maxAge * 0.8;
  }

  /**
   * Get the effective threshold for a task, adjusted for age (age polyethism).
   * Young ants prefer nursing tasks, older ants prefer foraging/guarding.
   */
  getEffectiveThreshold(taskType: TaskType): number {
    const baseThreshold = this.taskThresholds[taskType];
    const normalizedAge = Math.min(1, this.age / this.maxAge);

    // Age polyethism coefficients (positive = threshold increases with age)
    const ageCoefficients: Record<TaskType, number> = {
      [TaskType.FORAGE]: -0.2,       // Older ants more likely to forage
      [TaskType.FEED_LARVAE]: 0.25,  // Younger ants prefer nursing
      [TaskType.FEED_QUEEN]: 0.15,   // Younger ants prefer nursing
      [TaskType.CARRY_EGG]: 0.2,     // Younger ants prefer nursing
      [TaskType.TRANSPORT]: 0,       // No age effect
      [TaskType.GUARD]: -0.15,       // Older ants more likely to guard
      [TaskType.PATROL]: -0.1,       // Older ants more likely to patrol
      [TaskType.REPAIR]: 0,          // No age effect
      [TaskType.CLEAN]: 0.1,         // Slightly younger preference
      [TaskType.REST]: -0.1,         // Older ants need more rest
    };

    const ageModifier = ageCoefficients[taskType] * normalizedAge;
    return Math.max(0.05, Math.min(0.95, baseThreshold + ageModifier));
  }

  /**
   * Check if the ant can switch tasks (respects cooldown).
   */
  canSwitchTask(currentTime: number): boolean {
    return currentTime - this.lastTaskSwitchTime >= this.taskCooldown;
  }

  /**
   * Set a new task with cooldown to prevent jitter.
   */
  setTaskWithCooldown(newTask: AntTask, currentTime: number, cooldownMs: number = 2000): void {
    this.task = newTask;
    this.lastTaskSwitchTime = currentTime;
    this.taskCooldown = cooldownMs;
    this.taskTimer = 0;
  }

  isAlive(): boolean {
    return this.state !== AntState.DEAD && this.health > 0;
  }

  addMemory(memory: AntMemory): void {
    this.memories.push(memory);
    // Keep only recent memories (last 20)
    if (this.memories.length > 20) {
      this.memories.shift();
    }
  }

  addRelationship(antId: number, type: AntRelationship['type']): void {
    const existing = this.relationships.find(r => r.antId === antId);
    if (existing) {
      existing.strength = Math.min(1, existing.strength + 0.1);
    } else {
      this.relationships.push({ antId, type, strength: 0.3 });
    }
  }

  getBiography(): string[] {
    const lines: string[] = [];
    const displayName = this.getDisplayName();

    lines.push(`${displayName}`);
    lines.push(`Role: ${this.role.charAt(0).toUpperCase() + this.role.slice(1)}`);
    lines.push(`Age: ${this.getAgeInDays()} days`);

    if (this.role === AntRole.FORAGER) {
      lines.push(`Foraging trips: ${this.stats.foragingTrips}`);
      lines.push(`Food gathered: ${this.stats.foodGathered} units`);
    } else if (this.role === AntRole.SOLDIER) {
      lines.push(`Threats repelled: ${this.stats.threatsRepelled}`);
      if (this.scarred) {
        lines.push(`Battle-scarred from past encounters`);
      }
    } else if (this.role === AntRole.NURSE) {
      lines.push(`Eggs nursed: ${this.stats.eggsNursed}`);
    }

    if (this.isElderly()) {
      lines.push(`One of the colony's elders`);
    }

    if (this.isPhilosopher) {
      lines.push(`Sometimes pauses, as if thinking...`);
    }

    return lines;
  }

  /**
   * Export ant data for transfer to another layer.
   * Returns a serializable representation of the ant's state.
   */
  export(): AntExportData {
    return {
      id: this.id,
      name: this.name,
      customName: this.customName,
      role: this.role,
      birthTick: this.birthTick,
      age: this.age,
      health: this.health,
      maxHealth: this.maxHealth,
      hunger: this.hunger,
      fatigue: this.fatigue,
      size: this.size,
      speed: this.speed,
      baseSpeed: this.baseSpeed,
      isPhilosopher: this.isPhilosopher,
      scarred: this.scarred,
      injured: this.injured,
      carryingFood: this.carryingFood,
      carriedFoodAmount: this.carriedFoodAmount,
      carriedFoodHue: this.carriedFoodHue,
      task: this.task,
      state: this.state,
      stats: { ...this.stats },
      position: { ...this.position },
      // Response-threshold model additions
      taskThresholds: { ...this.taskThresholds },
      taskCooldown: this.taskCooldown,
      lastTaskSwitchTime: this.lastTaskSwitchTime,
    };
  }

  /**
   * Import ant data from a transfer.
   * Updates this ant's state from the exported data.
   */
  import(data: AntExportData): void {
    this.customName = data.customName;
    this.age = data.age;
    this.health = data.health;
    this.hunger = data.hunger;
    this.fatigue = data.fatigue;
    this.scarred = data.scarred;
    this.injured = data.injured;
    this.carryingFood = data.carryingFood;
    this.carriedFoodAmount = data.carriedFoodAmount ?? 0;
    this.carriedFoodHue = data.carriedFoodHue ?? 20;
    this.task = data.task ?? AntTask.NONE;
    this.state = data.state;
    this.stats = { ...data.stats };
    this.position = { ...data.position };
    // Response-threshold model additions
    if (data.taskThresholds) {
      this.taskThresholds = { ...data.taskThresholds };
    }
    if (data.taskCooldown !== undefined) {
      this.taskCooldown = data.taskCooldown;
    }
    if (data.lastTaskSwitchTime !== undefined) {
      this.lastTaskSwitchTime = data.lastTaskSwitchTime;
    }
  }

  /**
   * Everything this ant is, for a snapshot.
   *
   * Deliberately not `export()`. That method is the *transfer* format — what an
   * ant carries through a portal — and `import()` is what a colony applies to an
   * ant coming home. Widening the pair would mean a returning forager had its
   * chamber, its tunnel progress and its interaction target overwritten with
   * whatever they were when it left, which is a behaviour change wearing the
   * costume of a serialisation fix.
   *
   * So this is a superset, and the transfer format stays exactly as narrow as it
   * was. `maxAge`, `baseSpeed`, `size` and `maxHealth` are absent for a different
   * reason: they come from the role attributes, which are configuration rather
   * than state, and a restore reads them the same way a birth does.
   */
  save(): AntSnapshot {
    return {
      ...this.export(),
      // Fixed at construction, and *not* recoverable from the role attributes: an
      // ant that transitioned keeps the lifespan and base speed of the role it was
      // born into, while `role` says what it is now. Rebuilding a former nurse as
      // a forager would quietly make it faster and shorter-lived than the ant that
      // was saved. `maxAge` is `Infinity` for a queen, which JSON cannot hold.
      maxAge: jsonNumber(this.maxAge),
      previousRoles: this.previousRoles.map(r => ({ ...r, stats: { ...r.stats } })),
      lastTransitionTime: this.lastTransitionTime,
      repairTarget: this.repairTarget ? { ...this.repairTarget } : null,
      seekingEntrance: this.seekingEntrance,
      previousState: this.previousState,
      targetPosition: this.targetPosition ? { ...this.targetPosition } : null,
      relationships: this.relationships.map(r => ({ ...r })),
      memories: this.memories.map(m => ({ ...m, position: { ...m.position } })),
      currentStimulusResponse: this.currentStimulusResponse,
      carryingEgg: this.carryingEgg,
      taskTimer: this.taskTimer,
      alarmPosition: this.alarmPosition ? { ...this.alarmPosition } : null,
      scentPreference: this.scentPreference,
      currentChamberId: this.currentChamberId,
      currentTunnelId: this.currentTunnelId,
      recentTunnels: [...this.recentTunnels],
      tunnelProgress: this.tunnelProgress,
      movingTowardEnd: this.movingTowardEnd,
      chamberEntryTime: this.chamberEntryTime,
      lastChamberChangeId: this.lastChamberChangeId,
      avoidChambersUntil: Array.from(this.avoidChambersUntil.entries())
        .sort((a, b) => a[0] - b[0]),
      interactionTargetId: this.interactionTargetId,
      interactionProgress: this.interactionProgress,
      pickupChamberId: this.pickupChamberId,
    };
  }

  /** Apply a snapshot taken by `save()`. */
  load(data: AntSnapshot): void {
    this.import(data);

    // Four fields the transfer format carries and `import()` deliberately does
    // not apply, because a colony receiving one of its own ants back already
    // knows how big it is. A restore does not know: the queen is larger than her
    // attributes say, exactly one ant in the colony is the philosopher, an
    // injured ant is slower than its role, and all three would come back as the
    // role's defaults. This is the kind of loss that hashes equal at the moment
    // of restore and shows up four ticks later as a different ant contemplating.
    this.size = data.size;
    this.speed = data.speed;
    this.maxHealth = data.maxHealth;
    this.isPhilosopher = data.isPhilosopher;

    this.previousRoles = data.previousRoles.map(r => ({ ...r, stats: { ...r.stats } }));
    this.lastTransitionTime = data.lastTransitionTime;
    this.repairTarget = data.repairTarget ? { ...data.repairTarget } : null;
    this.seekingEntrance = data.seekingEntrance;

    this.previousState = data.previousState;
    this.targetPosition = data.targetPosition ? { ...data.targetPosition } : null;
    this.relationships = data.relationships.map(r => ({ ...r }));
    this.memories = data.memories.map(m => ({ ...m, position: { ...m.position } }));
    this.currentStimulusResponse = data.currentStimulusResponse;
    this.carryingEgg = data.carryingEgg;
    this.taskTimer = data.taskTimer;
    this.alarmPosition = data.alarmPosition ? { ...data.alarmPosition } : null;
    this.scentPreference = data.scentPreference;
    this.currentChamberId = data.currentChamberId;
    this.currentTunnelId = data.currentTunnelId;
    this.recentTunnels = [...data.recentTunnels];
    this.tunnelProgress = data.tunnelProgress;
    this.movingTowardEnd = data.movingTowardEnd;
    this.chamberEntryTime = data.chamberEntryTime;
    this.lastChamberChangeId = data.lastChamberChangeId;
    this.avoidChambersUntil = new Map(data.avoidChambersUntil);
    this.interactionTargetId = data.interactionTargetId;
    this.interactionProgress = data.interactionProgress;
    this.pickupChamberId = data.pickupChamberId;
  }

  /**
   * Rebuild an ant from a snapshot.
   *
   * Careful with the stream: the constructor rolls task thresholds that `load()`
   * immediately overwrites, so a throwaway generator absorbs those draws. Drawing
   * them from the colony's stream instead would advance it once per ant, and a
   * restored colony would diverge from an uninterrupted one on the next tick.
   */
  static fromSnapshot(
    data: AntSnapshot,
    roleAttributes?: Record<AntRole, AntRoleAttributes>
  ): Ant {
    const base = roleAttributes ?? DEFAULT_ANT_ROLE_ATTRIBUTES;
    // `maxAge` and `baseSpeed` are readonly and set from the attributes of the role
    // passed to the constructor, so they reach a restored ant the only way they
    // can: through attributes carrying the saved values for that role.
    const attrs: Record<AntRole, AntRoleAttributes> = {
      ...base,
      [data.role]: {
        ...base[data.role],
        maxAge: fromJsonNumber(data.maxAge, Infinity),
        baseSpeed: data.baseSpeed,
      },
    };
    const ant = new Ant(
      data.id,
      data.name,
      data.role,
      data.position,
      data.birthTick,
      new Sfc32Rng(`restore/${data.id}`),
      attrs
    );
    ant.load(data);
    return ant;
  }

}

/**
 * Serializable representation of an ant for transfer between layers.
 */
export interface AntExportData {
  id: number;
  name: string;
  customName: string | null;
  role: AntRole;
  birthTick: number;
  age: number;
  health: number;
  maxHealth: number;
  hunger: number;
  fatigue: number;
  size: number;
  speed: number;
  baseSpeed: number;
  isPhilosopher: boolean;
  scarred: boolean;
  injured: boolean;
  carryingFood: boolean;
  carriedFoodAmount: number;
  carriedFoodHue?: number;
  task: AntTask;
  state: AntState;
  stats: AntStats;
  position: Position;
  // Response-threshold model additions
  taskThresholds?: TaskThresholds;
  taskCooldown?: number;
  lastTaskSwitchTime?: number;
}

/**
 * Everything an ant is. See `Ant.save()`.
 *
 * A superset of the transfer format, with every field required — a snapshot is
 * written and read by the same version of the code, so an absent field is a bug
 * rather than an old save.
 */
export interface AntSnapshot extends AntExportData {
  /** Null for a queen, whose lifespan is `Infinity` and JSON's is not. */
  maxAge: number | null;
  previousRoles: PreviousRole[];
  lastTransitionTime: number | null;
  repairTarget: RepairTarget | null;
  seekingEntrance: boolean;
  previousState: AntState;
  targetPosition: Position | null;
  relationships: AntRelationship[];
  memories: AntMemory[];
  currentStimulusResponse: number;
  carryingEgg: boolean;
  taskTimer: number;
  alarmPosition: Position | null;
  scentPreference: ScentType;
  currentChamberId: number | null;
  currentTunnelId: number | null;
  recentTunnels: number[];
  tunnelProgress: number;
  movingTowardEnd: boolean;
  chamberEntryTime: number;
  lastChamberChangeId: number | null;
  /** Chamber id → sim-time to avoid it until, ascending by chamber. */
  avoidChambersUntil: Array<[number, number]>;
  interactionTargetId: number | null;
  interactionProgress: number;
  pickupChamberId: number | null;
}

/**
 * Deep copy of a transfer record.
 *
 * A surfaced ant carries one of these as `originalData`, and a snapshot that
 * shared it with the live world would be a document a later tick could edit.
 */
export function cloneAntExport(data: AntExportData): AntExportData {
  return {
    ...data,
    stats: { ...data.stats },
    position: { ...data.position },
    taskThresholds: data.taskThresholds ? { ...data.taskThresholds } : undefined,
  };
}

// Procedural name generator
export class AntNameGenerator {
  private static prefixes = [
    'Kira', 'Vex', 'Mira', 'Rex', 'Nova', 'Zeph', 'Luna', 'Sol',
    'Astra', 'Lyra', 'Vira', 'Nyx', 'Ora', 'Tera', 'Petra', 'Vera',
    'Cira', 'Dex', 'Flux', 'Gex', 'Hex', 'Ix', 'Jex', 'Kex',
  ];

  private static titles = [
    'the Swift', 'the Bold', 'the Steady', 'the Wise', 'the Strong',
    'the Patient', 'the Vigilant', 'the Enduring', 'the Brave',
  ];

  static generate(role: AntRole, id: number, rng: Rng): string {
    const prefix = this.prefixes[id % this.prefixes.length];

    // Most ants just get prefix-ID format
    if (rng.next() > 0.95) {
      // Rare: titled name
      const title = rng.pick(this.titles)!;
      return `${prefix} ${title}`;
    }

    // Standard: Name-ID format (like Kira-1184)
    return `${prefix}-${id}`;
  }

  static generateQueenName(generation: number): string {
    const numerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
    const queenNames = ['Venatrix', 'Regina', 'Magna', 'Prima', 'Domina'];
    const name = queenNames[generation % queenNames.length];
    const numeral = numerals[Math.min(generation, numerals.length - 1)];
    return `${name} ${numeral}`;
  }

  static generateSpecialName(rng: Rng): string {
    const specialNames = [
      'Old Map',
      'Glorious Mandate IX',
      'The Philosopher',
      'First Light',
      'Last Watch',
      'Deep Walker',
      'Trail Keeper',
    ];
    return rng.pick(specialNames)!;
  }
}
